import { useEffect, useState } from "react";
import api, { apiError, money } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { AHeader, ACard, ATable, btnPrimary, inputDark } from "@/components/shared/AdminKit";
import { useConfirm } from "@/context/ConfirmContext";
import { StatusBadge } from "@/components/shared/Badges";
import { fmtDate } from "@/components/shared/DashKit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Smartphone, Plus, Send, RotateCcw, FlaskConical, ChevronDown, KeyRound, Copy } from "lucide-react";

const opName = (config, code) => {
  for (const c of config?.countries || []) for (const o of c.operators) if (o.code === code) return `${c.flag} ${c.name} — ${o.name}`;
  return code;
};

export default function AdminPawaPay() {
  const { t } = useContent();
  const [config, setConfig] = useState(null);
  const [txns, setTxns] = useState([]);
  const [ops, setOps] = useState([]);
  const [testNums, setTestNums] = useState(null);
  const [signingKey, setSigningKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showManual, setShowManual] = useState(false);
  const [showPayout, setShowPayout] = useState(false);
  const [showMemo, setShowMemo] = useState(false);
  const [busy, setBusy] = useState(null);
  const confirm = useConfirm();

  const load = () => {
    Promise.all([
      api.get("/admin/pawapay/config"),
      api.get("/admin/pawapay/transactions"),
      api.get("/admin/pawapay/operations").catch(() => ({ data: [] })),
      api.get("/admin/pawapay/test-numbers").catch(() => ({ data: null })),
      api.get("/admin/pawapay/signing-key").catch(() => ({ data: null })),
    ]).then(([c, tx, o, tn, sk]) => { setConfig(c.data); setTxns(tx.data); setOps(o.data); setTestNums(tn.data); setSigningKey(sk.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggle = async (kind, code, enabled) => {
    try { await api.put("/admin/pawapay/toggle", { kind, code, enabled }); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const refund = async (tr) => {
    if (!(await confirm({ title: t("admin.pp.refund_title", "Rembourser via PawaPay"), description: t("admin.pp.refund_desc", "Rembourser {a} sur le Mobile Money du client via PawaPay ?").replace("{a}", money(tr.amount)), confirmLabel: t("admin.pp.refund_ok", "Rembourser"), cancelLabel: t("admin.pp.cancel", "Annuler") }))) return;
    const debit = await confirm({ title: t("admin.pp.debit_title", "Débiter le portefeuille ?"), description: t("admin.pp.debit_desc", "Débiter aussi le portefeuille USD du client de ce montant ?"), confirmLabel: t("admin.pp.debit_ok", "Débiter le portefeuille"), cancelLabel: t("admin.pp.debit_no", "Ne pas débiter") });
    setBusy(tr.id);
    try { await api.post("/admin/pawapay/refund", { payment_id: tr.id, debit_wallet: debit }); toast.success(t("admin.pp.refund_initiated", "Remboursement PawaPay initié")); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>;
  const isReal = config?.demo === false;

  return (
    <div>
      <AHeader title={t("admin.pp.title", "PawaPay — Mobile Money")} subtitle={t("admin.pp.subtitle", "Opérateurs africains, dépôts, payouts, remboursements et transactions.")}
        action={<div className="flex gap-2">
          {isReal && <button data-testid="pawapay-payout-btn" onClick={() => setShowPayout(true)} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"><Send className="h-4 w-4" /> {t("admin.pp.send_payout", "Envoyer un payout")}</button>}
          <button data-testid="pawapay-add-manual-btn" onClick={() => setShowManual(true)} className={btnPrimary}><Plus className="h-4 w-4" /> {t("admin.pp.add", "Ajouter")}</button>
        </div>} />

      <div className="mb-4 flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900 px-4 py-3 text-sm">
        <Smartphone className="h-4 w-4 text-accent" />
        <span className="text-slate-300">{t("admin.pp.environment", "Environnement :")} <span className="font-medium text-white">{config?.env}</span></span>
        {config?.demo
          ? <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-400">SANDBOX</span>
          : <span className="ml-2 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-400">{t("admin.pp.real", "RÉEL")}</span>}
      </div>

      {signingKey?.configured && (
        <ACard title={<span className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-accent" /> {t("admin.pp.signing_key", "Clé publique de signature")} ({signingKey.algorithm})</span>} className="mb-6">
          <div className="space-y-3" data-testid="pawapay-signing-key">
            <p className="text-xs text-slate-400">{t("admin.pp.signing_hint_1", "Collez cette clé publique dans le Dashboard PawaPay →")} <span className="text-slate-200">Security → Add public key</span>{t("admin.pp.signing_hint_2", ". Nommez la clé exactement")} <code className="rounded bg-slate-800 px-1 text-slate-200">{signingKey.key_id}</code> {t("admin.pp.signing_hint_3", "(identique au keyid des requêtes signées). La clé privée reste stockée uniquement côté serveur.")}</p>
            <pre className="max-h-56 overflow-auto rounded-lg border border-slate-800 bg-slate-950 p-3 font-mono text-[11px] leading-relaxed text-slate-300" data-testid="pawapay-signing-key-pem">{signingKey.public_key}</pre>
            <button onClick={() => { navigator.clipboard.writeText(signingKey.public_key); toast.success(t("admin.pp.pubkey_copied", "Clé publique copiée")); }} data-testid="pawapay-copy-signing-key" className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-xs font-medium text-white hover:bg-slate-800"><Copy className="h-3.5 w-3.5" /> {t("admin.pp.copy_pubkey", "Copier la clé publique")}</button>
          </div>
        </ACard>
      )}

      {testNums?.sandbox && (
        <ACard className="mb-6">
          <button onClick={() => setShowMemo((s) => !s)} data-testid="pawapay-memo-toggle" className="flex w-full items-center justify-between text-left">
            <span className="flex items-center gap-2 font-medium text-white"><FlaskConical className="h-4 w-4 text-accent" /> {t("admin.pp.test_numbers", "Numéros de test sandbox (succès / échec)")}</span>
            <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${showMemo ? "rotate-180" : ""}`} />
          </button>
          {showMemo && (
            <div className="mt-4 space-y-3">
              <p className="text-xs text-slate-400">{t("admin.pp.test_hint", "Utilisez ces numéros (sandbox uniquement) dans le champ téléphone pour simuler chaque résultat. ⚠️ Signatures signées : activez-les dans le Dashboard PawaPay, collez la clé publique dans")} <code className="text-slate-300">PAWAPAY_CALLBACK_PUBLIC_KEY_PEM</code> {t("admin.pp.test_hint_2", "puis passez")} <code className="text-slate-300">PAWAPAY_ENFORCE_SIGNATURE=true</code> {t("admin.pp.test_hint_3", "une fois l'app déployée sur une URL stable.")}</p>
              <div className="overflow-x-auto">
                <ATable head={[t("admin.pp.th.operator", "Opérateur"), "Dépôt ✓ COMPLETED", "Dépôt ⏳ SUBMITTED", "Dépôt ✗ (échecs)", "Payout ✓"]}>
                  {Object.entries(testNums.numbers).map(([code, d]) => (
                    <tr key={code} className="border-b border-slate-800/60 last:border-0" data-testid={`memo-row-${code}`}>
                      <td className="px-3 py-2 text-slate-200">{opName(config, code)}</td>
                      <td className="px-3 py-2 font-mono text-xs text-emerald-400">{d.deposit?.completed || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-amber-400">{d.deposit?.submitted || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-red-400">{(d.deposit?.failed || []).map((f) => `${f[0]} (${f[1]})`).join(", ") || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-emerald-400">{d.payout?.completed || "—"}</td>
                    </tr>
                  ))}
                </ATable>
              </div>
            </div>
          )}
        </ACard>
      )}

      <ACard title={t("admin.pp.countries_ops", "Pays & opérateurs")} className="mb-6">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {config?.countries?.map((c) => (
            <div key={c.code} className="rounded-lg border border-slate-800 bg-slate-950/40 p-4" data-testid={`pawapay-country-${c.code}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-medium text-white"><span className="text-lg">{c.flag}</span> {c.name}</div>
                <Switch checked={c.enabled !== false} onCheckedChange={(v) => toggle("country", c.code, v)} data-testid={`pawapay-toggle-country-${c.code}`} />
              </div>
              <div className="mt-3 space-y-2">
                {c.operators.map((o) => (
                  <div key={o.code} className="flex items-center justify-between rounded border border-slate-800 px-3 py-1.5 text-sm">
                    <span className="text-slate-200">{o.name}{o.currency ? <span className="ml-1 text-xs text-slate-500">· {o.currency}</span> : null}</span>
                    <Switch checked={o.enabled !== false && c.enabled !== false} disabled={c.enabled === false}
                      onCheckedChange={(v) => toggle("operator", o.code, v)} data-testid={`pawapay-toggle-operator-${o.code}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </ACard>

      <ACard title={t("admin.pp.deposits", "Dépôts Mobile Money")} className="mb-6">
        {txns.length === 0 ? <div className="py-10 text-center text-slate-400">{t("admin.pp.no_deposit", "Aucun dépôt.")}</div> : (
          <ATable head={[t("admin.pp.th.date", "Date"), t("admin.pp.th.user", "Utilisateur"), t("admin.pp.th.operator", "Opérateur"), t("admin.pp.th.phone", "Téléphone"), t("admin.pp.th.amount", "Montant"), t("admin.pp.th.amount_local", "Montant local"), t("admin.pp.th.status", "Statut"), t("admin.pp.th.provider", "Fournisseur"), t("admin.pp.th.source", "Source"), t("admin.pp.th.action", "Action")]}>
            {txns.map((tr) => (
              <tr key={tr.id} className="border-b border-slate-800/60 last:border-0" data-testid={`pawapay-txn-${tr.id}`}>
                <td className="px-3 py-3 text-slate-400">{fmtDate(tr.created_at)}</td>
                <td className="px-3 py-3 text-slate-400">{tr.user_email}</td>
                <td className="px-3 py-3">{tr.meta?.flag} {tr.meta?.operator_name}</td>
                <td className="px-3 py-3 font-mono text-xs text-slate-400">{tr.meta?.phone || "—"}</td>
                <td className="px-3 py-3 font-medium">{money(tr.amount)}</td>
                <td className="px-3 py-3 text-slate-300">{tr.meta?.amount_local != null ? `${tr.meta.amount_local} ${tr.meta.currency_local || ""}` : "—"}</td>
                <td className="px-3 py-3"><StatusBadge status={tr.status} /></td>
                <td className="px-3 py-3 text-xs text-slate-500">{tr.meta?.provider_status || "—"}</td>
                <td className="px-3 py-3 text-xs text-slate-500">{tr.manual ? t("admin.pp.manual", "Manuel") : t("admin.pp.client", "Client")}</td>
                <td className="px-3 py-3">
                  {isReal && tr.status === "completed" && tr.credited && !tr.manual && tr.meta?.deposit_id
                    ? <button onClick={() => refund(tr)} disabled={busy === tr.id} data-testid={`pawapay-refund-${tr.id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-2.5 py-1 text-xs text-slate-200 hover:bg-slate-800 disabled:opacity-50"><RotateCcw className="h-3 w-3" /> {t("admin.pp.refund_ok", "Rembourser")}</button>
                    : <span className="text-slate-600">—</span>}
                </td>
              </tr>
            ))}
          </ATable>
        )}
      </ACard>

      {ops.length > 0 && (
        <ACard title={t("admin.pp.payouts_refunds", "Payouts & Remboursements")}>
          <ATable head={[t("admin.pp.th.date", "Date"), t("admin.pp.th.type", "Type"), t("admin.pp.th.beneficiary", "Bénéficiaire / Dépôt"), t("admin.pp.th.amount", "Montant"), t("admin.pp.th.amount_local", "Montant local"), t("admin.pp.th.status", "Statut"), t("admin.pp.th.provider", "Fournisseur")]}>
            {ops.map((o) => (
              <tr key={o.id} className="border-b border-slate-800/60 last:border-0" data-testid={`pawapay-op-${o.id}`}>
                <td className="px-3 py-3 text-slate-400">{fmtDate(o.created_at)}</td>
                <td className="px-3 py-3"><span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${o.kind === "payout" ? "bg-sky-500/20 text-sky-400" : "bg-purple-500/20 text-purple-300"}`}>{o.kind === "payout" ? "PAYOUT" : t("admin.pp.refund_short", "REMBOURS.")}</span></td>
                <td className="px-3 py-3 text-slate-300">{o.flag} {o.operator_name} {o.phone ? <span className="font-mono text-xs text-slate-500">· {o.phone}</span> : null}</td>
                <td className="px-3 py-3 font-medium">{money(o.amount_usd)}</td>
                <td className="px-3 py-3 text-slate-300">{o.amount_local != null ? `${o.amount_local} ${o.currency_local || ""}` : "—"}</td>
                <td className="px-3 py-3"><StatusBadge status={o.status} /></td>
                <td className="px-3 py-3 text-xs text-slate-500">{o.provider_status || (o.failure_reason ? o.failure_reason : "—")}</td>
              </tr>
            ))}
          </ATable>
        </ACard>
      )}

      {showManual && <ManualModal config={config} onClose={() => setShowManual(false)} onDone={() => { setShowManual(false); load(); }} />}
      {showPayout && <PayoutModal config={config} onClose={() => setShowPayout(false)} onDone={() => { setShowPayout(false); load(); }} />}
    </div>
  );
}

function PayoutModal({ config, onClose, onDone }) {
  const { t } = useContent();
  const [form, setForm] = useState({ amount: "", country: "", operator: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const selectedCountry = config?.countries?.find((c) => c.code === form.country);

  const submit = async () => {
    if (!form.amount || !form.country || !form.operator || !form.phone) return toast.error(t("admin.pp.fields_required", "Champs requis manquants"));
    setSaving(true);
    try {
      const { data } = await api.post("/admin/pawapay/payout", { ...form, amount: Number(form.amount) });
      toast.success(t("admin.pp.payout_sent", "Payout envoyé ({a} {c})").replace("{a}", data.amount_local).replace("{c}", data.currency_local));
      onDone();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6" onClick={(e) => e.stopPropagation()} data-testid="pawapay-payout-modal">
        <div className="font-display text-lg font-bold text-white">{t("admin.pp.send_payout_mm", "Envoyer un payout Mobile Money")}</div>
        <p className="mt-1 text-xs text-slate-400">{t("admin.pp.payout_conv", "Le montant saisi en USD est converti en devise locale au taux du jour.")}</p>
        <div className="mt-4 space-y-3">
          <div><label className="text-xs text-slate-400">{t("admin.pp.amount_usd", "Montant (USD)")}</label>
            <input type="number" data-testid="payout-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={`mt-1 ${inputDark}`} placeholder="10" /></div>
          <div><label className="text-xs text-slate-400">{t("admin.pp.country", "Pays")}</label>
            <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v, operator: "" })}>
              <SelectTrigger data-testid="payout-country" className="mt-1 border-slate-700 bg-slate-800 text-white"><SelectValue placeholder={t("admin.pp.country", "Pays")} /></SelectTrigger>
              <SelectContent>{config?.countries?.map((c) => <SelectItem key={c.code} value={c.code}>{c.flag} {c.name}</SelectItem>)}</SelectContent>
            </Select></div>
          {selectedCountry && (
            <div><label className="text-xs text-slate-400">{t("admin.pp.operator", "Opérateur")}</label>
              <Select value={form.operator} onValueChange={(v) => setForm({ ...form, operator: v })}>
                <SelectTrigger data-testid="payout-operator" className="mt-1 border-slate-700 bg-slate-800 text-white"><SelectValue placeholder={t("admin.pp.operator", "Opérateur")} /></SelectTrigger>
                <SelectContent>{selectedCountry.operators.map((o) => <SelectItem key={o.code} value={o.code}>{o.name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <div><label className="text-xs text-slate-400">{t("admin.pp.beneficiary_phone", "Téléphone du bénéficiaire")}</label>
            <input data-testid="payout-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={`mt-1 ${inputDark}`} placeholder="260763456789" /></div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800">{t("admin.pp.cancel", "Annuler")}</button>
          <button onClick={submit} disabled={saving} data-testid="payout-submit" className={btnPrimary}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("admin.pp.send", "Envoyer")}</button>
        </div>
      </div>
    </div>
  );
}

function ManualModal({ config, onClose, onDone }) {
  const { t } = useContent();
  const [form, setForm] = useState({ user_email: "", amount: "", country: "", operator: "", phone: "", credit: true });
  const [saving, setSaving] = useState(false);
  const selectedCountry = config?.countries?.find((c) => c.code === form.country);

  const submit = async () => {
    if (!form.user_email || !form.amount || !form.country || !form.operator) return toast.error(t("admin.pp.fields_required", "Champs requis manquants"));
    setSaving(true);
    try {
      await api.post("/admin/pawapay/transactions", { ...form, amount: Number(form.amount) });
      toast.success(t("admin.pp.txn_added", "Transaction Mobile Money ajoutée"));
      onDone();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6" onClick={(e) => e.stopPropagation()} data-testid="pawapay-manual-modal">
        <div className="font-display text-lg font-bold text-white">{t("admin.pp.add_txn", "Ajouter une transaction Mobile Money")}</div>
        <div className="mt-4 space-y-3">
          <div><label className="text-xs text-slate-400">{t("admin.pp.client_email", "Email du client")}</label>
            <input data-testid="pawapay-manual-email" value={form.user_email} onChange={(e) => setForm({ ...form, user_email: e.target.value })} className={`mt-1 ${inputDark}`} placeholder="client@email.com" /></div>
          <div><label className="text-xs text-slate-400">{t("admin.pp.amount_usd", "Montant (USD)")}</label>
            <input type="number" data-testid="pawapay-manual-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={`mt-1 ${inputDark}`} placeholder="50" /></div>
          <div><label className="text-xs text-slate-400">{t("admin.pp.country", "Pays")}</label>
            <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v, operator: "" })}>
              <SelectTrigger data-testid="pawapay-manual-country" className="mt-1 border-slate-700 bg-slate-800 text-white"><SelectValue placeholder={t("admin.pp.country", "Pays")} /></SelectTrigger>
              <SelectContent>{config?.countries?.map((c) => <SelectItem key={c.code} value={c.code}>{c.flag} {c.name}</SelectItem>)}</SelectContent>
            </Select></div>
          {selectedCountry && (
            <div><label className="text-xs text-slate-400">{t("admin.pp.operator", "Opérateur")}</label>
              <Select value={form.operator} onValueChange={(v) => setForm({ ...form, operator: v })}>
                <SelectTrigger data-testid="pawapay-manual-operator" className="mt-1 border-slate-700 bg-slate-800 text-white"><SelectValue placeholder={t("admin.pp.operator", "Opérateur")} /></SelectTrigger>
                <SelectContent>{selectedCountry.operators.map((o) => <SelectItem key={o.code} value={o.code}>{o.name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <div><label className="text-xs text-slate-400">{t("admin.pp.phone_opt", "Téléphone (optionnel)")}</label>
            <input data-testid="pawapay-manual-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={`mt-1 ${inputDark}`} placeholder="970000000" /></div>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <Switch checked={form.credit} onCheckedChange={(v) => setForm({ ...form, credit: v })} data-testid="pawapay-manual-credit" />
            {t("admin.pp.credit_now", "Créditer immédiatement le portefeuille")}
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800">{t("admin.pp.cancel", "Annuler")}</button>
          <button onClick={submit} disabled={saving} data-testid="pawapay-manual-submit" className={btnPrimary}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("admin.pp.save", "Enregistrer")}</button>
        </div>
      </div>
    </div>
  );
}
